import React, { useState } from 'react';
import { SendOutlined, CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  TemplateDropdown,
  MessageTextarea,
  CharCounter,
  SendButton,
  SMS_TEMPLATES
} from './styled';
import DialerService from '../../services/DialerService';

const MAX_SMS_LENGTH = 160;

const QuickSmsComposer = ({ contact, phoneNumber }) => {
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  
  // Handle template selection
  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplate(templateId);
    
    if (templateId) {
      const template = SMS_TEMPLATES.find(t => t.id.toString() === templateId);
      if (template) {
        setMessage(template.text);
      }
    }
    
    // Reset states
    setSent(false);
    setError(null);
  };
  
  // Handle message change
  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    setSent(false);
    setError(null);
  };
  
  // Handle send
  const handleSend = async () => {
    if (!message.trim() || !phoneNumber) return;
    
    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length < 7) {
      setError('Invalid phone number');
      return;
    }
    
    setSending(true);
    setError(null);
    
    try {
      await DialerService.sendSms({
        to: digits,
        message: message.trim(),
        contactId: contact?.id,
        contactName: contact?.name
      });
      
      setSent(true);
      // Reset after 3 seconds
      setTimeout(() => {
        setSent(false);
        setMessage('');
        setSelectedTemplate('');
      }, 3000);
    } catch (err) {
      console.error('Error sending SMS:', err);
      setError(err.message || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };
  
  const charCount = message.length;
  const isOverLimit = charCount > MAX_SMS_LENGTH;
  const canSend = message.trim().length > 0 && !isOverLimit && !sending && !sent;
  
  return (
    <div>
      {/* Template Dropdown */}
      <TemplateDropdown>
        <select 
          value={selectedTemplate} 
          onChange={handleTemplateChange}
        >
          <option value="">Select a template...</option>
          {SMS_TEMPLATES.map((template) => (
            <option key={template.id} value={template.id}>
              {template.text.substring(0, 50)}...
            </option>
          ))}
        </select>
      </TemplateDropdown>
      
      {/* Message Input */}
      <MessageTextarea
        value={message}
        onChange={handleMessageChange}
        placeholder="Type your message..."
        maxLength={MAX_SMS_LENGTH * 2} // Allow typing but warn
      />
      
      {/* Character Counter */}
      <CharCounter $over={isOverLimit}>
        {charCount}/{MAX_SMS_LENGTH}
        {isOverLimit && ' (message will be split)'}
      </CharCounter>
      
      {/* Error Message */}
      {error && (
        <div style={{ 
          color: '#ff4d4f', 
          fontSize: 12, 
          marginTop: 8,
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}
      
      {/* Send Button */}
      <SendButton
        onClick={handleSend}
        disabled={!canSend}
        $success={sent}
      >
        {sending ? (
          <>
            <LoadingOutlined spin />
            Sending...
          </>
        ) : sent ? (
          <>
            <CheckOutlined />
            Sent!
          </>
        ) : (
          <>
            <SendOutlined />
            Send SMS
          </>
        )}
      </SendButton>
    </div>
  );
};

export default QuickSmsComposer;
